import { createFileRoute } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { Mail, Clock, ShieldCheck, ArrowRight } from 'lucide-react'
import { Container, SectionLabel } from '@/components/umbrella/ui'
import { CONTACT_EMAIL, resolveSite } from '@/lib/site'
import QatarContact from '@/components/qatar/QatarContact'

export const Route = createFileRoute('/contact')({
  component: ContactRoute,
})

function ContactRoute() {
  // The Qatar face is a customer page (fraud line, MOI), not a sales page.
  return resolveSite() === 'qatar' ? <QatarContact /> : <ContactPage />
}

const AUDIENCES = [
  'State agency',
  'Defense credit union',
  'Community organization',
  'Something else',
]

function ContactPage() {
  const [audience, setAudience] = useState(AUDIENCES[0])

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)
    const name = String(data.get('name') ?? '')
    const org = String(data.get('org') ?? '')
    const email = String(data.get('email') ?? '')
    const message = String(data.get('message') ?? '')

    const subject = `ScamDefense inquiry: ${audience}`
    const body = [
      `Name: ${name}`,
      `Organization: ${org}`,
      `Email: ${email}`,
      `Audience: ${audience}`,
      '',
      message,
    ].join('\n')

    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`
  }

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden border-b border-slate-100">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_60%_at_80%_20%,rgba(37,99,235,0.07),transparent_60%)]" />
        <Container className="grid gap-14 py-20 lg:grid-cols-2 lg:py-24">
          {/* Left: pitch + what to expect */}
          <div>
            <SectionLabel>Contact</SectionLabel>
            <h1 className="mt-4 text-4xl font-semibold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl">
              Let’s protect your community.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
              Tell us who you serve and what you’re trying to achieve. We’ll show
              you how a tailored ScamDefense deployment could work for your
              organization, with no obligation.
            </p>

            <ul className="mt-10 space-y-6">
              <ExpectItem Icon={Mail} title="Talk to a real person">
                Your message reaches our team directly, not an automated sales funnel.
              </ExpectItem>
              <ExpectItem Icon={Clock} title="A quick response">
                We aim to reply within two business days.
              </ExpectItem>
              <ExpectItem Icon={ShieldCheck} title="No obligation">
                We’ll give you an honest view of whether ScamDefense is the right fit.
              </ExpectItem>
            </ul>

            <p className="mt-10 text-sm text-slate-500">
              Prefer email? Reach us directly at{' '}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-medium text-blue-600 hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
            </p>
          </div>

          {/* Right: form */}
          <div className="rounded-3xl border border-slate-200/80 bg-white p-7 shadow-sm sm:p-9">
            <form onSubmit={handleSubmit} className="space-y-5">
              <Field label="Your name" name="name" placeholder="Jane Doe" required />
              <Field label="Organization" name="org" placeholder="Acme Credit Union" required />
              <Field label="Email" name="email" type="email" placeholder="jane@example.org" required />

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Who do you serve?
                </label>
                <div className="flex flex-wrap gap-2">
                  {AUDIENCES.map(a => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setAudience(a)}
                      className={`rounded-lg border px-3.5 py-2 text-sm font-medium transition ${
                        audience === a
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-slate-300 text-slate-600 hover:border-slate-400'
                      }`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  How can we help?
                </label>
                <textarea
                  name="message"
                  rows={4}
                  placeholder="Tell us a little about what you’re looking for."
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px] text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <button
                type="submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-blue-700"
              >
                Send message
                <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-center text-xs text-slate-400">
                Opens your email app with the details ready to send.
              </p>
            </form>
          </div>
        </Container>
      </section>
    </div>
  )
}

function ExpectItem({
  Icon,
  title,
  children,
}: {
  Icon: typeof Mail
  title: string
  children: React.ReactNode
}) {
  return (
    <li className="flex items-start gap-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-[15px] font-semibold text-slate-900">{title}</p>
        <p className="mt-0.5 text-sm leading-relaxed text-slate-600">{children}</p>
      </div>
    </li>
  )
}

function Field({
  label,
  name,
  type = 'text',
  placeholder,
  required,
}: {
  label: string
  name: string
  type?: string
  placeholder?: string
  required?: boolean
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <input
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px] text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      />
    </div>
  )
}
